import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Pagination,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { getPurchaseReturnReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { getRefundValues } from "../../../../util/function";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./BillingReturn.css";

const tableHdrs = [
  "returnDate",
  "invoiceNumber",
  "vendorName",
  "taxRefund",
  "totalRefund",
];

const BillingPurchaseReturnReport = ({ returnType = "Purchase", setReturnType }) => {
  const dispatch = useDispatch();
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const { purchaseReturn } = useSelector((state) => state?.report);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getPurchaseReturnReportData("?filter=debit&type=debit"));
  }, [dispatch]);

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const getRequiredTableList = () => {
    return (purchaseReturn?.data ?? [])?.filter(
      (li) => li?.creditSave?.toLowerCase() === "debit",
    );
  };

  const calculateTotalTaxRefund = () => {
    const list = getRequiredTableList() ?? [];
    const total = list.reduce((sum, item) => {
      const refund = getRefundValues(
        item?.paymentDetails?.filter((pd) => pd?.returnQuantity > 0),
      );
      return sum + Number(refund?.tax ?? 0);
    }, 0);
    return total.toFixed(2);
  };

  const summaryCards = [
    { title: "Returns (period)", value: getRequiredTableList()?.length ?? 0 },
    { title: "Total tax refund", value: `₹${calculateTotalTaxRefund()}` },
  ];

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((getRequiredTableList()?.length ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    setPage(selectedPage);
  };

  const getTableData = () => {
    return (getRequiredTableList() ?? [])
      ?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
      ?.map((li) => {
        const invoices = [
          li?.petShopInvoiceNumber ?? "",
          li?.treatmentInvoiceNumber ?? "",
          li?.invoiceNumbers ?? "",
        ]?.filter(Boolean);
        return {
          ...li,
          returnDate: moment(
            new Date(li?.updatedDate ?? li?.createdDate),
          ).format("DD MMM YYYY"),
          invoiceNumber: invoices?.length > 0 ? invoices?.join(", ") : "-",
          vendorName: li?.vendorName ?? "",
          taxRefund: Number(
            getRefundValues(
              li?.paymentDetails?.filter((pd) => pd?.returnQuantity > 0),
            )?.tax,
          )?.toFixed(2),
          totalRefund: Number(
            getRefundValues(
              li?.paymentDetails?.filter((pd) => pd?.returnQuantity > 0),
            )?.total,
          )?.toFixed(2),
        };
      });
  };

  const handleExportExcel = () => {
    const tableData = getRequiredTableList() ?? [];
    const exportData = tableData.map((row) => {
      const invoices = [
        row?.petShopInvoiceNumber ?? "",
        row?.treatmentInvoiceNumber ?? "",
        row?.invoiceNumbers ?? "",
      ]?.filter(Boolean);
      return {
        "Return Date": moment(new Date(row?.updatedDate ?? row?.createdDate)).format("DD MMM YYYY"),
        "Invoice No.": invoices?.length > 0 ? invoices?.join(", ") : "-",
        "Supplier Name": row?.vendorName ?? "",
        "Tax Refund": Number(
          getRefundValues(
            row?.paymentDetails?.filter((pd) => pd?.returnQuantity > 0),
          )?.tax,
        )?.toFixed(2),
        "Total Refund": Number(
          getRefundValues(
            row?.paymentDetails?.filter((pd) => pd?.returnQuantity > 0),
          )?.total,
        )?.toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Billing Purchase Returns");
    XLSX.writeFile(workbook, `Billing_Purchase_Returns_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  return (
    <div className="billing-return-container">
      {/* Title */}
      <h2 className="billing-return-title">Returns report</h2>

      {/* Read-only Banner */}
      {/* <div className="billing-return-banner">Read only</div> */}

      {/* Top Right Toggle Bar */}
      <div className="billing-return-toggle-bar">
        <div className="billing-return-toggle-group">
          <button
            type="button"
            className={`billing-return-toggle-btn ${returnType === "Sales" ? "active" : ""}`}
            onClick={() => setReturnType && setReturnType("Sales")}
          >
            Sales
          </button>
          <button
            type="button"
            className={`billing-return-toggle-btn ${returnType === "Purchase" ? "active" : ""}`}
            onClick={() => setReturnType && setReturnType("Purchase")}
          >
            Purchase
          </button>
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="billing-return-export-group">
        <button type="button" className="billing-return-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="billing-return-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (2 Cards) */}
      <div className="billing-return-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="billing-return-card" key={idx}>
            <h4 className="billing-return-card-title">{card.title}</h4>
            <div className="billing-return-card-value-container">
              <h2 className="billing-return-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Drill-down Section with Top Right Edit Columns */}
      <div className="flex-row-between-align-center mv10">
        <div className="billing-return-drilldown-header">Drill-down — returns</div>
        <div
          className="flex-row-ali-cen cursor"
          onClick={handleEditColumn}
        >
          <EditOutlinedIcon sx={w20h20BlueIcon} />
          <Typography className="blue-color fs16 font-medium ml5">
            Edit columns ({crntTblHeders?.length})
          </Typography>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Showing {getTableData()?.length ?? 0} of {getRequiredTableList()?.length ?? 0} rows for the selected filters
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((getRequiredTableList()?.length ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default BillingPurchaseReturnReport;
